<template>
  <v-container fluid class="bg-background pa-0">
    <!-- HERO -->
    <v-container fluid class="hero-section py-16 text-center">
      <h1 class="hero-title">
        List with <span class="un">un</span>Booked
      </h1>
      <p class="hero-sub mt-6">
        Register below as an unBooked travel partner to add your listings and start earning commission today
      </p>
    </v-container>

    <v-container max-width="1100" class="py-12">
      <v-card elevation="3" rounded="xl">
        <v-card-text class="pa-6">
          <v-form ref="formRef" v-model="isValid" lazy-validation @submit.prevent="handleSubmit">
            <v-row dense>
              <!-- Agency / IATA -->
              <v-col cols="12" md="8">
                <v-text-field v-model="form.agencyName" label="What is your agency/business name? *"
                  :rules="[rules.required]" variant="outlined" density="comfortable" prepend-inner-icon="mdi-domain" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="form.itaNumber" label="IATA or CLIA Number *" :rules="[rules.required]"
                  variant="outlined" density="comfortable" prepend-inner-icon="mdi-badge-account" />
              </v-col>

              <!-- Name -->
              <v-col cols="12" md="6">
                <v-text-field v-model="form.firstName" label="First name *" :rules="[rules.required]" variant="outlined"
                  prepend-inner-icon="mdi-account" density="comfortable" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.lastName" label="Last name *" :rules="[rules.required]" variant="outlined"
                  prepend-inner-icon="mdi-account" density="comfortable" />
              </v-col>

              <!-- Contact -->
              <v-col cols="12" md="6">
                <v-text-field v-model="form.email" label="Email *" :rules="[rules.required, rules.email]"
                  variant="outlined" density="comfortable" prepend-inner-icon="mdi-email" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.phone" label="Phone number *" :rules="[rules.required]" variant="outlined"
                  density="comfortable" prepend-inner-icon="mdi-phone" />
              </v-col>

              <!-- Address -->
              <v-col cols="12" md="12">
                <v-text-field v-model="form.city" label="Street *" :rules="[rules.required]" variant="outlined"
                  prepend-inner-icon="mdi-map" density="comfortable" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="form.city" label="City *" :rules="[rules.required]" variant="outlined"
                  prepend-inner-icon="mdi-map-marker" density="comfortable" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="form.postalCode" label="Postal code *" :rules="[rules.required]"
                  prepend-inner-icon="mdi-map-marker" variant="outlined" density="comfortable" />
              </v-col>
              <v-col cols="12" md="4">
                <v-select v-model="form.region" :items="stateOptions" item-title="label" item-value="value"
                  label="Region/State *" :rules="[rules.required]" variant="outlined" density="comfortable"
                  prepend-inner-icon="mdi-map-marker" />
              </v-col>

              <!-- PLAN SELECTION -->
              <v-col cols="12">
                <div class="text-subtitle-1 font-weight-bold mb-2">Choose a plan *</div>
                <v-radio-group v-model="form.plan" :rules="[rules.required]" class="mb-2">
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-card :elevation="form.plan === 'standard' ? 6 : 2" rounded="lg" class="pa-4 h-100"
                        @click="form.plan = 'standard'" role="button">
                        <div class="d-flex align-center justify-space-between mb-2">
                          <div class="text-subtitle-2 font-weight-bold">Standard</div>
                          <v-radio value="standard" color="primary" />
                        </div>
                        <div class="text-caption text-medium-emphasis">$0 / mo</div>
                        <div class="text-caption">3% fee, basic support</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-card :elevation="form.plan === 'enhanced' ? 6 : 2" rounded="lg" class="pa-4 h-100"
                        @click="form.plan = 'enhanced'" role="button">
                        <div class="d-flex align-center justify-space-between mb-2">
                          <div class="text-subtitle-2 font-weight-bold">Enhanced</div>
                          <v-radio value="enhanced" color="primary" />
                        </div>
                        <div class="text-caption text-medium-emphasis">$100 / mo</div>
                        <div class="text-caption">1.75% fee, placement boost</div>
                      </v-card>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-card :elevation="form.plan === 'premium' ? 6 : 2" rounded="lg" class="pa-4 h-100"
                        @click="form.plan = 'premium'" role="button">
                        <div class="d-flex align-center justify-space-between mb-2">
                          <div class="text-subtitle-2 font-weight-bold">Premium</div>
                          <v-radio value="premium" color="primary" />
                        </div>
                        <div class="text-caption text-medium-emphasis">$175 / mo</div>
                        <div class="text-caption">1.25% fee, promo + manager</div>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-radio-group>
              </v-col>

              <!-- Upload instructions -->
              <v-col cols="12">
                <v-alert type="info" variant="tonal" border="start" class="mb-2">
                  Please upload the following documents using the fields below:
                  <strong>Signed Travel Advisor Agreement</strong>,
                  <strong>Seller of Travel License (if applicable)</strong>,
                  and <strong>W-9 Form</strong>.
                </v-alert>
              </v-col>

              <!-- Files -->
              <v-col cols="12" md="4">
                <v-file-input v-model="files.agreement" label="Signed Travel Advisor Agreement *"
                  accept=".pdf,.jpg,.jpeg,.png" :rules="[rules.requiredFile]" variant="outlined" density="comfortable"
                  prepend-inner-icon="mdi-file-document" />
              </v-col>
              <v-col cols="12" md="4">
                <v-file-input v-model="files.license" label="Seller of Travel License (optional)"
                  accept=".pdf,.jpg,.jpeg,.png" variant="outlined" density="comfortable"
                  prepend-inner-icon="mdi-file-certificate" />
              </v-col>
              <v-col cols="12" md="4">
                <v-file-input v-model="files.w9" label="W-9 Form *" accept=".pdf,.jpg,.jpeg,.png"
                  :rules="[rules.requiredFile]" variant="outlined" density="comfortable"
                  prepend-inner-icon="mdi-file-account" />
              </v-col>

              <!-- ACCOUNT CREATION -->
              <v-col cols="12" md="6">
                <v-text-field v-model="form.password" label="Create password" type="password"
                  :rules="[rules.passwordOrEmpty, rules.required]" variant="outlined" density="comfortable"
                  prepend-inner-icon="mdi-lock-outline" autocomplete="new-password" />
                <ul class="hint">
                  <li :class="passwordHasUppercase ? 'ok' : 'bad'">Must contain an uppercase letter</li>
                  <li :class="passwordHasLength ? 'ok' : 'bad'">Must be at least 6 characters</li>
                </ul>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.confirmPassword" label="Confirm password" type="password"
                  :rules="[rules.confirmPassword, rules.required]" variant="outlined" density="comfortable"
                  prepend-inner-icon="mdi-lock-check" autocomplete="new-password" />
                <ul class="hint">
                  <li :class="passwordsMatch ? 'ok' : 'bad'">Passwords match</li>
                </ul>
              </v-col>

              <!-- Submit -->
              <v-col cols="12" class="mt-2">
                <v-btn type="submit" color="primary" size="large" block :loading="submitting" :disabled="!isValid">
                  Submit
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>

      <v-snackbar v-model="snack.show" :color="snack.color" timeout="4000">
        {{ snack.msg }}
      </v-snackbar>
    </v-container>
  </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'
import { useRouter } from 'vue-router'

export default {
  name: 'AgentSignUpVuetify',
  data() {
    return {
      isValid: false,
      submitting: false,
      form: {
        agencyName: '',
        itaNumber: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        street: '',
        city: '',
        postalCode: '',
        region: '',
        plan: "standard",
        password: '',
        confirmPassword: ''
      },
      files: {
        agreement: null,
        license: null,
        w9: null
      },
      snack: { show: false, color: 'success', msg: '' },
      stateOptions: [
        { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
        { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
        { value: 'DC', label: 'District of Columbia' }
      ],
      rules: {
        required: v => (!!v || v === 0) || 'Required',
        email: v => /.+@.+\..+/.test(v) || 'Enter a valid email',
        requiredFile: v => (!!v && (Array.isArray(v) ? v.length > 0 : true)) || 'Required',

        // Password rules (optional fields): allow empty, else validate
        passwordOrEmpty: v => {
          if (!v) return true
          if (v.length < 6) return 'At least 6 characters'
          if (!/[A-Z]/.test(v)) return 'Must include an uppercase letter'
          return true
        },
        confirmPassword: v => {
          if (!v && !this.form.password) return true
          return v === this.form.password || 'Passwords must match'
        }
      }
    }
  },

  computed: {
    passwordHasUppercase() { return /[A-Z]/.test(this.form.password) },
    passwordHasLength() { return (this.form.password || '').length >= 6 },
    passwordsMatch() { return this.form.password && this.form.password === this.form.confirmPassword }
  },

  methods: {
    newUuid() {
      return (crypto && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = (Math.random() * 16) | 0
          const v = c === 'x' ? r : (r & 0x3) | 0x8
          return v.toString(16)
        })
    },
    async handleSubmit() {
      const formOk = await this.$refs.formRef.validate()
      if (!formOk.valid)

        var pendingId = null

      this.submitting = true
      try {
        // If a password is provided, create a Supabase auth account (optional)
        if (this.form.password) {
          try {
            const { data: signupData, error: signupError } = await supabase.auth.signUp({
              email: this.form.email,
              password: this.form.password,
              options: {
                data: {
                  firstName: this.form.firstName,
                  lastName: this.form.lastName,
                  isAgent: true
                }
              }
            })
            // If already registered, continue (we won't block the agent request)
            if (signupError) {
              throw signupError
            }

            pendingId = signupData?.user.id

          } catch (err) {
            // Show but don't stop the agent submission
            console.warn('Auth signup skipped/failed:', err?.message || err)
          }
        }

        // Upload files (bucket: 'agent-documents') — unchanged
        const uploads = await this.uploadDocuments()

        if (!pendingId) {
          pendingId = this.newUuid()
        }

        // Insert pending agent signup (includes desired plan; NEVER store password)
        const payload = {
          id: pendingId,
          agency_name: this.form.agencyName,
          ita_number: this.form.itaNumber,
          first_name: this.form.firstName,
          last_name: this.form.lastName,
          email: this.form.email,
          phone: this.form.phone,
          street: this.form.street,
          city: this.form.city,
          postal_code: this.form.postalCode,
          region: this.form.region,
          desired_plan: this.form.plan,
          advisor_agreement_url: uploads.agreementKey || null,
          travel_license_url: uploads.licenseKey || null,
          w9_url: uploads.w9Key || null,
          created_at: new Date().toISOString()
        }

        const { error } = await supabase
          .from('pending_agent_signups')
          .insert([payload])
          .select('*')

        if (error) throw error

        this.snack = { show: true, color: 'success', msg: 'Registration submitted! We will review your documents shortly.' }
        await useRouter().replace('/agent/signup/done')
      } catch (e) {
        console.error(e)
        this.snack = { show: true, color: 'error', msg: 'There was an error submitting your registration.' }
      } finally {
        this.submitting = false
      }
    },

    // (unchanged) uploadDocuments()
    async uploadDocuments() {
      const result = { agreementKey: null, licenseKey: null, w9Key: null }
      try {
        const files = {
          agreement: this.files?.agreement || null,
          license: this.files?.license || null,
          w9: this.files?.w9 || null,
        }
        const wanted = Object.entries(files)
          .filter(([, f]) => !!f)
          .map(([slot, f]) => ({ slot, ext: (f.name?.split('.').pop() || 'pdf').toLowerCase() }))
        if (!wanted.length) return result

        const { data: authData } = await supabase.auth.getUser()
        const scopeId = this.pendingId || authData?.user?.id || this.userId || 'anonymous'

        const { data, error } = await supabase.functions.invoke('create_agent_doc_uploads', {
          body: { pending_id: scopeId, files: wanted, bucket: 'agent-documents' }
        })
        if (error) throw new Error(error.message || JSON.stringify(error))

        const bucket = data.bucket || 'agent-documents'
        const uploads = data.uploads || {}

        const upOne = async (slot, file) => {
          const entry = uploads?.[slot]
          if (!entry?.path || !entry?.token) throw new Error(`Missing token/path for ${slot}`)
          const { error: upErr } = await supabase.storage
            .from(bucket)
            .uploadToSignedUrl(entry.path, entry.token, file, { contentType: file.type || 'application/octet-stream' })
          if (upErr) throw upErr
          return entry.path
        }

        if (files.agreement) result.agreementKey = await upOne('agreement', files.agreement)
        if (files.license) result.licenseKey = await upOne('license', files.license)
        if (files.w9) result.w9Key = await upOne('w9', files.w9)

        return result
      } catch (err) {
        console.warn('Upload failed:', err)
        return result
      }
    },

    resetForm() {
      this.form = {
        agencyName: '',
        itaNumber: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        city: '',
        postalCode: '',
        region: '',
        plan: '',
        password: '',
        confirmPassword: ''
      }
      this.files = { agreement: null, license: null, w9: null }
      this.$refs.formRef?.resetValidation?.()
    }
  }
}
</script>

<style scoped>
.hint {
  margin: 6px 0 0;
  padding-left: 0;
  list-style: none;
}

.hint li {
  font-size: .78rem;
}

.hint .ok {
  color: #2e7d32;
}

.hint .bad {
  color: #c62828;
}
</style>
